import { useMutation } from "@tanstack/react-query"
import urlBase64ToUint8Array from "./urlBase64ToUint8Array"
import useSnackbarStore from "stores/snackbar"
import useSessionStore from "stores/session"
import useGlobalStore from "stores/global"
import useT from "./useT"
import useRouterStore from "stores/routerStore"
import getLocationName from "./getLocationName"
import { checkAndUpdateIOSVersion } from "./getIOSVersion"
import { event } from "./gtag"

type SubscribeResult = { ok: true } | { ok: false }

const safeStringify = (value: unknown) => {
  try {
    return JSON.stringify(value)
  } catch {
    return JSON.stringify({ message: "Failed to serialize diagnostics" })
  }
}

const useNotifications = () => {
  const t = useT()
  const uid = useSessionStore((state) => state.user.uid)
  const email = useSessionStore((state) => state.user.email)
  const locale = useRouterStore((state) => state.router?.locale)
  const toggleToast = useSnackbarStore((store) => store.toggleToast)
  const toggleSnackbar = useSnackbarStore((store) => store.toggleSnackbar)
  const setNotificationsStatus = useGlobalStore(
    (store) => store.setNotificationsStatus,
  )

  const fail = (message: string, toast = false) => {
    setNotificationsStatus(false)
    const toggle = toast ? toggleToast : toggleSnackbar
    toggle({
      color: "red",
      text: message,
    })
    return { ok: false as const }
  }

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      subscription,
      location,
    }: {
      subscription: PushSubscription
      location: string
    }) => {
      const response = await fetch("/api/save-subscription", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subscription,
          uid,
          email,
          locale,
          date: new Date().getTime(),
          location:
            process.env.NODE_ENV == "development" ? "development" : location,
        }),
      })

      const responseText = await response.text()
      let responseData: unknown = null

      if (responseText) {
        try {
          responseData = JSON.parse(responseText)
        } catch {
          responseData = responseText
        }
      }

      if (!response.ok) {
        throw new Error(
          `save-subscription failed with ${response.status}: ${safeStringify(
            responseData,
          )}`,
        )
      }

      return responseData
    },
    onSuccess() {
      setNotificationsStatus(true) // Update the subscription status
      event({
        action: "notifications_sub_success",
        category: "notifications",
        label: "subscribed to notifications successfully",
      })
    },
  })

  const NEXT_PUBLIC_VAPID_PUBLIC_KEY: string =
    process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY!

  const subscribe = async (): Promise<SubscribeResult> => {
    try {
      checkAndUpdateIOSVersion(t)

      if (
        !("Notification" in window) ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window)
      ) {
        return fail(
          "Notifications are not supported on this device or browser.",
        )
      }

      const registration =
        (await navigator.serviceWorker.getRegistration()) || undefined

      if (!registration?.active) {
        event({
          action: "notifications_sub_failed",
          category: "notifications",
          label: "failed to subscribed to notifications",
          value: JSON.stringify({ result: "inactive-service-worker" }),
        })
        return fail(
          "Notification setup is still finishing. Please try again in a moment.",
        )
      }

      // Triggers popup to request access to send notifications
      const result = await window.Notification.requestPermission()

      if (result === "denied") {
        toggleSnackbar({
          color: "blue",
          text: t.notifications.enableDenied,
        })
        event({
          action: "notifications_sub_denied",
          category: "notifications",
          label: "denied to subscribed to notifications",
          value: JSON.stringify({ result }),
        })
        return { ok: false }
      }

      if (result !== "granted") {
        event({
          action: "notifications_sub_failed",
          category: "notifications",
          label: "failed to subscribed to notifications",
          value: JSON.stringify({ result }),
        })
        return fail(t.snackbar.error)
      }

      const existingSubscription =
        await registration.pushManager.getSubscription()
      const subscription =
        existingSubscription ||
        (await registration.pushManager.subscribe({
          applicationServerKey: urlBase64ToUint8Array(
            NEXT_PUBLIC_VAPID_PUBLIC_KEY,
          ) as BufferSource,
          userVisibleOnly: true,
        }))

      const location = await getLocationName().catch(() => "unknown")

      try {
        await mutateAsync({ subscription, location })
      } catch (error) {
        event({
          action: "notifications_sub_failed_server",
          category: "notifications",
          label: "failed to subscribed to notifications",
          value: safeStringify(error).slice(0, 500),
        })
        return fail(
          "Notification subscription failed to send data to save-subscription api",
          true,
        )
      }

      registration
        .showNotification(t.notifications.success, {
          tag: "https://www.appollow.net/?ref=Notification success",
          body: t.notifications.successMsg,
        } as NotificationOptions)
        .catch(() => undefined)

      return { ok: true }
    } catch (error) {
      event({
        action: "notifications_sub_failed",
        category: "notifications",
        label: "failed to subscribed to notifications",
        value: safeStringify(error).slice(0, 500),
      })
      return fail(t.snackbar.error)
    }
  }

  return { subscribe, isPending } // Return the subscription status
}

export default useNotifications
